import { useState, useEffect, useMemo } from 'react'
import ReactTable from './react-table'
import { useCustomers } from 'services/queries/use-customers'
import { Box, CircularProgress, Typography } from '@mui/material'
import useColumns from './use-columns'
import { useCustomersFromEmail } from 'services/queries/use-customers-from-email'

const CustomersTable = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  const memoizedColumns = useColumns()

  // Get all customers
  const getCustomersQuery = useCustomers()
  const allCustomers = getCustomersQuery.data

  // API search (searches by user email)
  const searchTerm = debouncedSearchQuery.trim()
  const apiSearchQuery = useCustomersFromEmail(searchTerm)
  const apiSearchResults = apiSearchQuery?.data

  // Debounce search input
  useEffect(() => {
    const debounceFn = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => {
      clearTimeout(debounceFn)
    }
  }, [searchQuery])

  // Client-side filtering for name, responsible_user, local_id
  const clientFilteredCustomers = useMemo(() => {
    const data = Array.isArray(allCustomers) ? allCustomers : []

    if (!searchTerm) {
      return data
    }

    const query = searchTerm.toLowerCase()
    return data.filter((customer) => {
      const name = (customer.name || '').toLowerCase()
      const responsibleUser = (customer.responsible_user || '').toLowerCase()
      const localId = (customer.local_id || '').toLowerCase()
      const id = String(customer.id || '').toLowerCase()

      return (
        name.includes(query) ||
        responsibleUser.includes(query) ||
        localId.includes(query) ||
        id.includes(query)
      )
    })
  }, [allCustomers, searchTerm])

  // Combine results: merge API results (email search) with client-filtered results
  const memoizedData = useMemo(() => {
    if (!searchTerm) {
      return Array.isArray(allCustomers) ? allCustomers : []
    }

    // Start with client-filtered results
    const clientResults = clientFilteredCustomers

    // Add API results (these are customers found by user email)
    const apiResults = Array.isArray(apiSearchResults) ? apiSearchResults : []

    // Merge: combine both, removing duplicates by customer id
    const seenIds = new Set(clientResults.map((c) => c.id))
    const uniqueApiResults = apiResults.filter((c) => !seenIds.has(c.id))

    return [...clientResults, ...uniqueApiResults]
  }, [allCustomers, clientFilteredCustomers, apiSearchResults, searchTerm])

  const isSearchLoading = Boolean(searchTerm && apiSearchQuery?.isLoading)

  if (getCustomersQuery.isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '300px' }}>
        <CircularProgress size={30} />
        <Typography variant="body1">Loading customers...</Typography>
      </Box>
    )
  }

  return (
    <ReactTable
      data={memoizedData}
      columns={memoizedColumns}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      isSearchLoading={isSearchLoading}
    />
  )
}

export default CustomersTable
